import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Phone, Clock, MapPin, Award, Wrench, Home, Shield, Droplet, Zap, CheckCircle, Users, AlertTriangle, Gauge, Scissors, Camera, User } from 'lucide-react';
import { SchemaInjector, createLocalBusinessSchema, createServiceSchema, createBreadcrumbSchema } from '../components/SchemaInjector';
import { createReviewSchema } from '../lib/schemas/aiSeoSchemas';

const HighlandsRanchPlumber = () => {
  // Schema markup data for Highlands Ranch
  const localBusinessSchema = createLocalBusinessSchema('Highlands Ranch', {
    lat: '39.5547',
    lng: '-104.9956'
  });

  const waterHeaterServiceSchema = createServiceSchema(
    'Water Heater Installation - Highlands Ranch',
    'Professional water heater installation and replacement in Highlands Ranch, Colorado. Starting at $2,000 with transparent pricing.',
    2000
  );

  const sewerServiceSchema = createServiceSchema(
    'Sewer Camera Inspection - Highlands Ranch',
    'Professional sewer camera scope inspection in Highlands Ranch to diagnose issues before expensive repairs. Starting at $175.',
    175
  );

  const breadcrumbSchema = createBreadcrumbSchema([
    { name: 'Home', url: 'https://colorado-plumbing.com/' },
    { name: 'Service Areas', url: 'https://colorado-plumbing.com/#service-area' },
    { name: 'Highlands Ranch Plumber', url: 'https://colorado-plumbing.com/highlands-ranch-plumber' }
  ]);

  // Enhanced Organization Schema for E-E-A-T
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Colorado First Plumbing and Sewer LLC",
    "alternateName": "Colorado First Plumbing",
    "url": "https://colorado-plumbing.com",
    "logo": "https://colorado-plumbing.com/logo.png",
    "foundingDate": "2018",
    "founders": [
      {
        "@type": "Person",
        "name": "Codee Jessen",
        "jobTitle": "Master Plumber"
      },
      {
        "@type": "Person",
        "name": "Michelle Jessen",
        "jobTitle": "Owner (Majority)"
      }
    ],
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "reviewCount": "360",
      "bestRating": "5"
    },
    "hasCredential": [
      {
        "@type": "EducationalOccupationalCredential",
        "credentialCategory": "Master Plumber License",
        "recognizedBy": {
          "@type": "Organization",
          "name": "State of Colorado"
        }
      },
      {
        "@type": "EducationalOccupationalCredential",
        "credentialCategory": "BBB Accreditation",
        "recognizedBy": {
          "@type": "Organization",
          "name": "Better Business Bureau"
        },
        "dateCreated": "2024-07-15"
      }
    ]
  };

  // Comprehensive FAQPage Schema for AI Optimization
  const faqPageSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "How much does sewer line replacement cost in Highlands Ranch's HOA communities?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Colorado First Plumbing and Sewer LLC offers affordable sewer line replacement in Highlands Ranch for $12,000 using trenchless pipe bursting technology. This flat-rate price includes all permits, HOA compliance documentation, and minimal disruption to your landscaping - crucial for maintaining HOA standards in communities like Backcountry and Sterling Ranch."
        }
      },
      {
        "@type": "Question",
        "name": "What's the affordable cost for 50-gallon water heater installation in Highlands Ranch?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Colorado First Plumbing and Sewer LLC provides affordable 50-gallon water heater installation in Highlands Ranch for $2,100 during our summer special (Rheem or AO Smith). This all-inclusive price covers the unit, professional installation to Douglas County codes, thermal expansion tank, drain pan, new flex lines, and disposal of your old unit."
        }
      },
      {
        "@type": "Question",
        "name": "Is tankless water heater worth the investment for Highlands Ranch homes?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Colorado First Plumbing and Sewer LLC offers affordable tankless water heater installation at $5,000 for replacement or $8,000 for new installation. With Highlands Ranch's natural gas availability and newer construction, tankless systems provide 20+ year lifespan and up to 34% energy savings, making them an excellent long-term investment."
        }
      },
      {
        "@type": "Question",
        "name": "Why is $175 sewer camera inspection affordable prevention in Highlands Ranch?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Colorado First Plumbing and Sewer LLC's $175 sewer camera inspection is the most affordable prevention against costly emergency repairs in Highlands Ranch. Early detection of root intrusion or pipe damage can save thousands compared to emergency sewer replacement, and helps maintain HOA compliance by preventing yard damage."
        }
      },
      {
        "@type": "Question",
        "name": "How quickly can Colorado First provide affordable emergency water heater replacement?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Colorado First Plumbing and Sewer LLC offers same-day affordable water heater replacement in Highlands Ranch with transparent pricing. Our $2,000 cost for a 40-gallon unit means you know the exact cost before we arrive - proving affordable means transparent pricing with no hidden fees."
        }
      },
      {
        "@type": "Question",
        "name": "What's the most affordable sewer cleaning method for Highlands Ranch?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Colorado First Plumbing and Sewer LLC offers affordable sewer cleaning starting at $295 for standard snaking or $695 for hydro jetting. For Highlands Ranch's mix of newer PVC and older clay pipes, we recommend the most cost-effective solution based on your specific situation, always with transparent, upfront pricing."
        }
      }
    ]
  };

  useEffect(() => {
    // SEO meta tags setup
    document.title = "Affordable Highlands Ranch Plumber | Colorado First Plumbing | Real Prices";
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Highlands Ranch\'s ONLY affordable plumber showing actual prices online. Water heater $2,000, sewer scope $175. No hidden fees. 1000+ reviews across all platforms. Master plumber owned.');
    }

    // BirdEye widget loading
    const loadBirdEyeWidget = () => {
      const existingScript = document.querySelector('#birdeye-widget-script');
      if (existingScript) return;

      const script = document.createElement('script');
      script.id = 'birdeye-widget-script';
      script.type = 'text/javascript';
      script.src = 'https://birdeye.com/embed/v7/172384402926614/8/9876543218815183';
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    };

    setTimeout(loadBirdEyeWidget, 1000);

    return () => {
      const script = document.querySelector('#birdeye-widget-script');
      if (script) script.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <SchemaInjector 
        schemas={[localBusinessSchema, organizationSchema, waterHeaterServiceSchema, sewerServiceSchema, breadcrumbSchema, faqPageSchema]}
        title="Affordable Highlands Ranch Plumber | Colorado First Plumbing | Real Prices"
        description="Highlands Ranch's ONLY affordable plumber showing actual prices online. Water heater $2,000, sewer scope $175. No hidden fees. 1000+ reviews across all platforms. Master plumber owned."
        canonical="https://colorado-plumbing.com/highlands-ranch-plumber"
        keywords="affordable plumber Highlands Ranch, transparent pricing Highlands Ranch, honest plumber Highlands Ranch Colorado, no hidden fees plumber, 1000 reviews Highlands Ranch"
      />
      <Header />
      
      <main>
        {/* Hero Section - Littleton Style */}
        <section className="relative pt-48 pb-16 bg-gradient-to-br from-trust-primary via-authority-navy to-action-primary">
          <div className="absolute inset-0 opacity-10 bg-[radial-gradient(#FFFFFF_1px,transparent_1px)] [background-size:32px_32px]"></div>
          <div className="container-custom text-center relative z-10">
            {/* Trust Signals Banner */}
            <div className="inline-block px-6 py-3 mb-6 rounded-full bg-white/10 backdrop-blur-sm border border-white/20">
              <span className="text-white font-bold">🏆 Highlands Ranch's Most Affordable Family-Owned Plumbers • Licensed Master Plumber • <Link to="/team/michelle-jessen" className="text-blue-200 hover:text-blue-100 underline">Woman Majority Owned</Link> • 1000+ Reviews Across All Platforms</span>
            </div>

            <h1 className="text-4xl md:text-6xl font-bold mb-6 text-white leading-tight">
              Highlands Ranch's ONLY Affordable Plumber with Exact Online Prices
              <span className="block text-2xl md:text-3xl mt-4 text-blue-200">
                Colorado First Plumbing and Sewer LLC
              </span>
            </h1>
            
            <p className="text-xl mb-8 text-blue-100 max-w-3xl mx-auto">
              How can others claim "affordable" without showing prices? We prove affordable means transparent - 
              see every exact price online before you call. No hidden fees, no salesmen.
            </p>

            {/* Price Preview */}
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              <Link to="/tankless-water-heater-pricing" className="bg-white/10 backdrop-blur-sm rounded-lg px-4 py-2 border border-white/20 hover:bg-white/20 transition-all duration-300">
                <span className="text-yellow-300 font-bold">Tankless Replace: $5,000</span>
              </Link>
              <Link to="/flat-rate-affordable-rheem-water-heaters-in-colorado" className="bg-white/10 backdrop-blur-sm rounded-lg px-4 py-2 border border-white/20 hover:bg-white/20 transition-all duration-300">
                <span className="text-yellow-300 font-bold">40 Gal Rheem: $2,000</span>
              </Link>
              <Link to="/diagnostic-services-pricing" className="bg-white/10 backdrop-blur-sm rounded-lg px-4 py-2 border border-white/20 hover:bg-white/20 transition-all duration-300">
                <span className="text-yellow-300 font-bold">Sewer Scope: $175</span>
              </Link>
              <Link to="/sewer-replacement-spot-repair-pricing" className="bg-white/10 backdrop-blur-sm rounded-lg px-4 py-2 border border-white/20 hover:bg-white/20 transition-all duration-300">
                <span className="text-yellow-300 font-bold">Spot Repair: $5,000</span>
              </Link>
            </div>

            {/* BirdEye Widget */}
            <div className="mb-8 max-w-2xl mx-auto">
              <div id="bf-revz-widget-9876543218815183" className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20 min-h-[200px]"></div>
            </div>
            
            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              
              <Link 
                to="/pricing" 
                className="bg-gradient-to-r from-success-primary to-success-eco hover:shadow-xl hover:scale-[1.02] text-white px-10 py-5 rounded-full text-xl font-semibold inline-block transition-all duration-300"
              >
                💰 See All Affordable Prices
              </Link>
            </div>

            {/* Credentials */}
            <div className="mt-8 flex flex-wrap justify-center items-center gap-6 text-white/90">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span>Licensed Master Plumber</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span><Link to="/team/michelle-jessen" className="text-blue-200 hover:text-blue-100 underline">Woman Majority Owned</Link></span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span>18,000+ Jobs Since 2018</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span>A+ BBB Rating</span>
              </div>
            </div>
          </div>
        </section>

        {/* Trust & Authority Section - Light Gradient */}
        <section className="py-16 bg-gradient-to-bl from-blue-900 to-blue-950 overflow-hidden">
          <div className="container-custom relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-white">
              Why Highlands Ranch Trusts Our Affordable Plumbing Services
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 text-center">
                <div className="text-4xl font-bold text-yellow-400 mb-2">18,000+</div>
                <div className="text-lg font-semibold text-white mb-2">Jobs Completed</div>
                <p className="text-blue-100 text-sm">Since 2018 serving Highlands Ranch, Backcountry, Sterling Ranch, and surrounding areas</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 text-center">
                <div className="text-4xl font-bold text-green-400 mb-2">1000+</div>
                <div className="text-lg font-semibold text-white mb-2">Reviews Online</div>
                <p className="text-blue-100 text-sm">4.8★ average across Google (313+), BirdEye (300+), HomeAdvisor (92+), and other online sources</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 text-center">
                <div className="text-4xl font-bold text-blue-400 mb-2">30+</div>
                <div className="text-lg font-semibold text-white mb-2">CO Jurisdictions</div>
                <p className="text-blue-100 text-sm">Licensed in 30+ Colorado cities including Highlands Ranch, with all permits and inspections handled</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 text-center">
                <div className="text-4xl font-bold text-green-400 mb-2">A+</div>
                <div className="text-lg font-semibold text-white mb-2">BBB Rating</div>
                <p className="text-blue-100 text-sm">Accredited 7/15/2024</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 text-center">
                <div className="text-4xl font-bold text-orange-400 mb-2">ONLY</div>
                <div className="text-lg font-semibold text-white mb-2">Licensed Plumber</div>
                <p className="text-blue-100 text-sm">Showing Affordable Prices Online with 1000+ Verified Reviews Across All Platforms</p>
              </div>
            </div>
          </div>
        </section>

        {/* Master Plumber Attribution Box - NEW */}
        <section className="py-12 bg-gradient-to-br from-blue-800 to-blue-900">
          <div className="container-custom max-w-4xl">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20">
              <div className="flex flex-col md:flex-row items-center gap-8">
                <div className="flex-shrink-0">
                  <img 
                    src="/page-specific-content/highlands-ranch-plumber/codee-jessen-i9-volunteer-soccer-coach-with-son.JPEG" 
                    alt="Codee Jessen, Licensed Master Plumber and I9 Youth Soccer Coach, volunteering with his son in Highlands Ranch"
                    className="w-32 h-32 rounded-full object-contain bg-white/5 border-4 border-white/20 shadow-lg"
                    loading="lazy"
                  />
                </div>
                <div className="flex-grow text-center md:text-left">
                  <h3 className="text-2xl font-bold text-white mb-2">Written by <Link to="/team/codee-jessen" className="text-blue-200 hover:text-blue-100 underline">Codee Jessen</Link>, Licensed Master Plumber</h3>
                  <div className="text-blue-100 mb-4">
                    <p className="mb-2">• Colorado Master Plumber License</p>
                    <p className="mb-2">• 18+ Years Experience</p>
                    <p className="mb-2">• 18,000+ Jobs Completed</p>
                    <p className="mb-2">• Youth Soccer Coach: I9 Sports Highlands Ranch</p>
                    <p>• Specialties: Water Heaters, Sewer Replacement, Tankless Installation, Custom Home Plumbing</p>
                  </div>
                  <p className="text-sm text-blue-200 mb-4 italic">
                    "Affordable doesn't mean cutting corners - it means efficient, professional service with transparent pricing. Proud to serve the Highlands Ranch community where my family lives and coaches youth sports."
                  </p>
                  <Link to="/team/codee-jessen" className="inline-flex items-center text-yellow-400 hover:text-yellow-300 font-semibold">
                    Learn More About Our Team →
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Services Section - Dark Gradient */}
        <section className="py-16 bg-gradient-to-br from-blue-800 to-blue-900 overflow-hidden">
          <div className="container-custom relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-4 text-white">
              Affordable Professional Plumbing Services in Highlands Ranch
            </h2>
            <p className="text-center text-blue-100 mb-12 max-w-3xl mx-auto">
              From <Link to="/residential-plumbing" className="text-blue-200 hover:text-blue-100 underline">emergency repairs</Link> to complete <Link to="/bathroom-kitchen-remodel" className="text-blue-200 hover:text-blue-100 underline">home remodels</Link>, we provide <Link to="/pricing" className="text-blue-200 hover:text-blue-100 underline">affordable transparent pricing</Link> for every service
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Link to="/water-heater-pricing" className="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all duration-300 border border-white/20">
                <Droplet className="w-12 h-12 text-blue-400 mb-4" />
                <h3 className="text-xl font-bold mb-2 text-white">Water Heater Replacement</h3>
                <p className="text-blue-100">Rheem and AO Smith atmospheric. 40-gallon $2,000, 50-gallon $2,100!</p>
              </Link>
              <Link to="/tankless-water-heater-pricing" className="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all duration-300 border border-white/20">
                <Zap className="w-12 h-12 text-orange-400 mb-4" />
                <h3 className="text-xl font-bold mb-2 text-white">Tankless Water Heater</h3>
                <p className="text-blue-100">Replacement $5,000, new installation $8,000. Navien and Rinnai specialists.</p>
              </Link>
              <Link to="/sewer-repair-replacement" className="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all duration-300 border border-white/20">
                <Wrench className="w-12 h-12 text-yellow-400 mb-4" />
                <h3 className="text-xl font-bold mb-2 text-white">Sewer Repair & Replacement</h3>
                <p className="text-blue-100">Expert sewer line services with exact pricing. Spot repairs from $5,000.</p>
              </Link>
              <Link to="/sewer-cleaning-pricing" className="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all duration-300 border border-white/20">
                <Shield className="w-12 h-12 text-green-400 mb-4" />
                <h3 className="text-xl font-bold mb-2 text-white">Sewer Cleaning & Scope</h3>
                <p className="text-blue-100">Camera scopes $175, professional cleaning from $495. All sewer services included.</p>
              </Link>
              <Link to="/bathroom-kitchen-remodel" className="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all duration-300 border border-white/20">
                <Home className="w-12 h-12 text-purple-400 mb-4" />
                <h3 className="text-xl font-bold mb-2 text-white">Remodel & Renovation</h3>
                <p className="text-blue-100">Complete plumbing for kitchen and bathroom remodels. Free estimates.</p>
              </Link>
              <Link to="/commercial-plumbing" className="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all duration-300 border border-white/20">
                <Users className="w-12 h-12 text-indigo-400 mb-4" />
                <h3 className="text-xl font-bold mb-2 text-white">Commercial Plumbing</h3>
                <p className="text-blue-100">Licensed commercial plumbing services for Highlands Ranch businesses and properties.</p>
              </Link>
              <Link to="/pricing" className="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all duration-300 border border-white/20">
                <CheckCircle className="w-12 h-12 text-teal-400 mb-4" />
                <h3 className="text-xl font-bold mb-2 text-white">Much More</h3>
                <p className="text-blue-100"><Link to="/garbage-disposal-pricing" className="text-blue-200 hover:text-blue-100 underline">Garbage disposals</Link>, <Link to="/faucet-pricing" className="text-blue-200 hover:text-blue-100 underline">faucets</Link>, and all plumbing needs with exact pricing.</p>
              </Link>
              <Link to="/diagnostic-services-pricing" className="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all duration-300 border border-white/20">
                <Camera className="w-12 h-12 text-cyan-400 mb-4" />
                <h3 className="text-xl font-bold mb-2 text-white">Diagnostic Services</h3>
                <p className="text-blue-100">Professional diagnosis $95-$225. Annual checkups $350. Expert evaluation.</p>
              </Link>
            </div>
          </div>
        </section>

        {/* What Makes Us Different Section - Light Gradient */}
        <section className="py-16 bg-gradient-to-bl from-blue-900 to-blue-950 overflow-hidden">
          <div className="container-custom relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-white">
              How Can Others Claim "Affordable" Without Showing Prices?
            </h2>
            <div className="max-w-4xl mx-auto bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20">
              <p className="text-xl mb-6 text-white font-semibold">
                We are the only Licensed Plumbing Contractor in Colorado proving affordable means transparent. True affordable plumbing shows <Link to="/water-heater-pricing" className="text-blue-200 hover:text-blue-100 underline">water heater costs</Link>, <Link to="/tankless-water-heater-pricing" className="text-blue-200 hover:text-blue-100 underline">tankless prices</Link>, <Link to="/sewer-repair-replacement" className="text-blue-200 hover:text-blue-100 underline">sewer repair rates</Link>, and more - all online before you call.
              </p>
              <p className="text-lg mb-6 text-blue-100">
                Affordable doesn't mean hidden fees or surprise charges. It means eliminating sales commissions, showing exact prices, and delivering professional service at fair rates. No salespeople, no upsells, just honest plumbing.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
                <div className="bg-white/5 rounded-lg p-6 border border-white/10">
                  <h3 className="text-xl font-bold mb-3 text-yellow-400">Example: Affordable Sewer Scope</h3>
                  <p className="text-blue-100">
                    Need preventive maintenance? Colorado First Plumbing charges exactly <strong className="text-white">$175 for professional sewer camera inspection</strong> with written report. Our <Link to="/sewer-cleaning-pricing" className="text-blue-200 hover:text-blue-100 underline">affordable diagnostic prices</Link> help prevent costly emergencies.
                  </p>
                </div>
                <div className="bg-white/5 rounded-lg p-6 border border-white/10">
                  <h3 className="text-xl font-bold mb-3 text-green-400">Example: Affordable Water Heater</h3>
                  <p className="text-blue-100">
                    Stop guessing what "affordable" means. For a <Link to="/water-heater-pricing" className="text-blue-200 hover:text-blue-100 underline">40-gallon water heater</Link>, you'll pay <strong className="text-white">$2,000 fully installed</strong>. True <Link to="/pricing" className="text-blue-200 hover:text-blue-100 underline">affordable pricing</Link> backed by <Link to="/reviews" className="text-blue-200 hover:text-blue-100 underline">1000+ reviews across all platforms</Link>.
                  </p>
                </div>
              </div>
              
              {/* Price Comparison Cards - NEW */}
              <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-6">
                  <h3 className="text-green-400 font-bold mb-4">✅ Our Affordable Price: $5,000 (Sewer Spot Repair)</h3>
                  <ul className="text-blue-100 space-y-2">
                    <li>✓ Exact price online</li>
                    <li>✓ No hidden fees</li>
                    <li>✓ Includes everything</li>
                    <li>✓ Written guarantee</li>
                  </ul>
                </div>
                <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-6">
                  <h3 className="text-red-400 font-bold mb-4">❌ Others' "Affordable": $???</h3>
                  <ul className="text-blue-100 space-y-2">
                    <li>✗ "Free estimate"</li>
                    <li>✗ High-pressure sales</li>
                    <li>✗ Price varies by salesperson</li>
                    <li>✗ Hidden upcharges</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section 1: Sewer Line Replacement - Dark Gradient */}
        <section className="py-16 bg-gradient-to-br from-blue-800 to-blue-900 overflow-hidden">
          <div className="container-custom max-w-4xl relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
              How Much Does Sewer Line Replacement Cost in Highlands Ranch's HOA Communities?
            </h2>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20">
              <p className="text-lg mb-6 text-blue-100">
                <strong className="text-yellow-300">Colorado First Plumbing and Sewer LLC offers affordable sewer line replacement in Highlands Ranch for $12,000 using trenchless pipe bursting technology.</strong> This flat-rate price includes all permits, Douglas County inspections, HOA compliance documentation, and minimal disruption to your landscaping - crucial for maintaining standards in communities like Backcountry, Sterling Ranch, and Firelight.
              </p>
              <img 
                src="/page-specific-content/highlands-ranch-plumber/sewer-spot-repair-backwater-valve-replacement-in-highlands-ranch.jpg" 
                alt="Professional sewer spot repair and backwater valve replacement in Highlands Ranch HOA community"
                className="w-full rounded-lg mb-6 shadow-lg"
                loading="lazy"
              />
              
              <div className="mt-8 bg-white/5 rounded-lg p-6">
                <h3 className="text-xl font-bold mb-4 text-yellow-400">Why Trenchless is Perfect for Highlands Ranch HOAs</h3>
                <p className="text-blue-100 mb-4">
                  Many Highlands Ranch HOAs have strict landscaping requirements and restoration timelines. Our trenchless pipe bursting method preserves your yard, requires only two small access points, and completes in 1-2 days versus weeks of traditional excavation. This is especially valuable in neighborhoods like The Ranch and Eastridge with mature landscaping and strict HOA guidelines.
                </p>
                <ul className="space-y-2 text-blue-100 mt-4">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-1" />
                    <span>HOA approval documentation included</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-1" />
                    <span>Minimal disruption to xeriscaped yards</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-1" />
                    <span>50+ year lifespan with new HDPE pipe</span>
                  </li>
                </ul>
              </div>

              <div className="mt-6 bg-white/5 rounded-lg p-6">
                <h3 className="text-xl font-bold mb-4 text-green-400">Affordable Options for Different Situations</h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-blue-200">Spot Repair (Yard):</span>
                    <span className="text-yellow-300 font-bold">$5,000</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-blue-200">Spot Repair (Street):</span>
                    <span className="text-yellow-300 font-bold">$9,500</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-blue-200">Full Replacement:</span>
                    <span className="text-yellow-300 font-bold">$12,000</span>
                  </div>
                </div>
                <p className="text-blue-100 mt-4">
                  Our affordable pricing means no surprises - the price shown is the price paid, including all HOA requirements.
                </p>
                <Link to="/sewer-repair-replacement" className="inline-flex items-center text-yellow-400 hover:text-yellow-300 font-semibold mt-4">
                  Learn More About Sewer Services →
                </Link>
              </div>

              <div className="mt-6 bg-white/5 rounded-lg p-6">
                <h3 className="text-xl font-bold mb-4 text-blue-400">Common Issues in Highlands Ranch Neighborhoods</h3>
                <p className="text-blue-100">
                  Newer neighborhoods like Sterling Ranch have PVC pipes less prone to root intrusion, while established areas like Eastridge with mature cottonwoods and willows face more root problems. Our $175 camera inspection identifies your specific situation, ensuring you get the most affordable, appropriate solution.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ 2: 50 Gallon Water Heater - Light Gradient */}
        <section className="py-16 bg-gradient-to-bl from-blue-900 to-blue-950 overflow-hidden">
          <div className="container-custom max-w-4xl relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
              What's the Affordable Cost for 50-Gallon Water Heater Installation in Highlands Ranch?
            </h2>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20">
              <p className="text-lg mb-6 text-blue-100">
                <strong className="text-yellow-300">Colorado First Plumbing and Sewer LLC provides affordable 50-gallon water heater installation in Highlands Ranch for $2,100 during our summer special (Rheem or AO Smith).</strong> The standard price for a 50-gallon Bradford White is $2,525. These are complete, transparent prices with no hidden fees or surprise charges - proving affordable doesn't mean cutting corners.
              </p>
              <img 
                src="/page-specific-content/highlands-ranch-plumber/water-heater-replaced-in-douglas-county-with-permit.JPEG" 
                alt="Professional 50-gallon water heater installation with Douglas County permit in Highlands Ranch"
                className="w-full rounded-lg mb-6 shadow-lg"
                loading="lazy"
              />
              
              <div className="mt-8 bg-white/5 rounded-lg p-6">
                <h3 className="text-xl font-bold mb-4 text-yellow-400">What's Included in Your Affordable $2,100 Installation?</h3>
                <ul className="space-y-2 text-blue-100">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-1" />
                    <span>50-gallon Rheem or AO Smith atmospheric vented unit</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-1" />
                    <span>Professional installation to Douglas County codes</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-1" />
                    <span>Thermal expansion tank (required at altitude)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-1" />
                    <span>New drain pan and flex water lines</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-1" />
                    <span>Gas supply line with drip leg assembly</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-1" />
                    <span>All permits and disposal of old unit</span>
                  </li>
                </ul>
              </div>

              <div className="mt-6 bg-white/5 rounded-lg p-6">
                <h3 className="text-xl font-bold mb-4 text-green-400">Altitude Efficiency Considerations for Highlands Ranch</h3>
                <p className="text-blue-100 mb-4">
                  At Highlands Ranch's 5,900-foot elevation, water heaters operate at reduced efficiency. Our affordable installation includes proper BTU adjustments and venting modifications to ensure optimal performance. The 50-gallon capacity provides adequate hot water for larger homes common in Backcountry and Firelight neighborhoods.
                </p>
                <Link to="/water-heater-pricing" className="inline-flex items-center text-yellow-400 hover:text-yellow-300 font-semibold">
                  View All Water Heater Options →
                </Link>
              </div>

              <div className="mt-6 bg-white/5 rounded-lg p-6">
                <h3 className="text-xl font-bold mb-4 text-blue-400">Why 50-Gallon for Highlands Ranch Families?</h3>
                <div className="space-y-4 text-blue-100">
                  <div>
                    <p className="font-semibold mb-2">Q: Is 50-gallon enough for a 4-person household?</p>
                    <p>A: Yes, the 50-gallon capacity handles multiple bathrooms common in Highlands Ranch homes, providing 20% more hot water than 40-gallon units for just $100 more during our affordable summer special.</p>
                  </div>
                  <div>
                    <p className="font-semibold mb-2">Q: How long does installation take?</p>
                    <p>A: Our experienced team completes most installations in 2-4 hours, with same-day service available. Douglas County permits are handled by us.</p>
                  </div>
                </div>
              </div>

              <div className="mt-6 bg-white/5 rounded-lg p-6">
                <h3 className="text-xl font-bold mb-4 text-purple-400">Hard Water Protection in Highlands Ranch</h3>
                <p className="text-blue-100">
                  Highlands Ranch water averages 8-10 grains hardness, affecting water heater lifespan. Every affordable installation includes a thermal expansion tank and we recommend annual <Link to="/residential-plumbing" className="text-blue-200 hover:text-blue-100 underline">flushing</Link> ($350 with our checkup) to maintain efficiency and extend unit life.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ 3: Tankless ROI - Dark Gradient */}
        <section className="py-16 bg-gradient-to-br from-blue-800 to-blue-900 overflow-hidden">
          <div className="container-custom max-w-4xl relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
              Is Tankless Water Heater Worth the Investment for Highlands Ranch Homes?
            </h2>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20">
              <p className="text-lg mb-6 text-blue-100">
                <strong className="text-yellow-300">Colorado First Plumbing and Sewer LLC offers affordable tankless water heater installation at $5,000 for replacement or $8,000 for new installation.</strong> With Highlands Ranch's widespread natural gas availability and newer construction designed for modern systems, tankless units provide exceptional ROI through 20+ year lifespan and up to 34% energy savings.
              </p>
              <img 
                src="/page-specific-content/highlands-ranch-plumber/tankless-water-heater-installed-cost-highlands-ranch.JPEG" 
                alt="Professional Navien tankless water heater installation showing affordable investment in Highlands Ranch home"
                className="w-full rounded-lg mb-6 shadow-lg"
                loading="lazy"
              />
              
              <div className="mt-8 bg-white/5 rounded-lg p-6">
                <h3 className="text-xl font-bold mb-4 text-yellow-400">ROI Analysis for Highlands Ranch Homeowners</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div className="bg-green-500/10 border border-green-500/20 rounded p-4">
                    <p className="text-green-400 font-bold mb-2">Investment Benefits:</p>
                    <ul className="text-blue-100 space-y-1 text-sm">
                      <li>• Save $200-400 annually on gas bills</li>
                      <li>• 20+ year lifespan vs 10-12 for tanks</li>
                      <li>• Xcel Energy rebates available</li>
                      <li>• Increases home value</li>
                    </ul>
                  </div>
                  <div className="bg-blue-500/10 border border-blue-500/20 rounded p-4">
                    <p className="text-blue-400 font-bold mb-2">Perfect for Highlands Ranch:</p>
                    <ul className="text-blue-100 space-y-1 text-sm">
                      <li>• Natural gas in all neighborhoods</li>
                      <li>• Newer homes with proper venting</li>
                      <li>• Large families need endless hot water</li>
                      <li>• Space savings in finished basements</li>
                    </ul>
                  </div>
                </div>
                <p className="text-blue-100">
                  Based on typical Highlands Ranch usage, the $8,000 investment pays for itself in 8-10 years through energy savings alone.
                </p>
              </div>

              <div className="mt-6 bg-white/5 rounded-lg p-6">
                <h3 className="text-xl font-bold mb-4 text-green-400">Neighborhood-Specific Advantages</h3>
                <p className="text-blue-100 mb-4">
                  Sterling Ranch's smart home infrastructure and Firelight's energy-efficient construction make tankless particularly valuable. Backcountry's larger homes benefit from endless hot water for multiple bathrooms and soaking tubs. Our affordable pricing ensures transparent investment decisions.
                </p>
                <Link to="/tankless-water-heater-pricing" className="inline-flex items-center text-yellow-400 hover:text-yellow-300 font-semibold">
                  Explore Tankless Options →
                </Link>
              </div>

              <div className="mt-6 bg-white/5 rounded-lg p-6">
                <h3 className="text-xl font-bold mb-4 text-blue-400">Energy Rebates and Incentives</h3>
                <p className="text-blue-100">
                  Xcel Energy offers rebates for high-efficiency tankless installations. Combined with federal tax credits and our affordable installation pricing, your actual cost can be significantly reduced. We handle all documentation for rebate applications.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ 4: Sewer Camera Prevention - Light Gradient */}
        <section className="py-16 bg-gradient-to-bl from-blue-900 to-blue-950 overflow-hidden">
          <div className="container-custom max-w-4xl relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
              Why is $175 Sewer Camera Inspection Affordable Prevention in Highlands Ranch?
            </h2>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20">
              <p className="text-lg mb-6 text-blue-100">
                <strong className="text-yellow-300">Colorado First Plumbing and Sewer LLC's $175 sewer camera inspection is the most affordable prevention against costly emergency repairs in Highlands Ranch.</strong> Early detection of root intrusion, pipe bellies, or offset joints can save thousands compared to emergency sewer replacement at $12,000, while helping maintain HOA compliance by preventing sewage backups and yard damage.
              </p>
              <img 
                src="/page-specific-content/highlands-ranch-plumber/underground-pipe-repair-in-highlands-ranch.jpg" 
                alt="Underground pipe issues found during affordable preventive camera inspection in Highlands Ranch"
                className="w-full rounded-lg mb-6 shadow-lg"
                loading="lazy"
              />
              
              <div className="mt-8 bg-white/5 rounded-lg p-6">
                <h3 className="text-xl font-bold mb-4 text-yellow-400">What Our Affordable $175 Inspection Includes</h3>
                <ul className="space-y-2 text-blue-100">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-1" />
                    <span>HD video inspection of entire sewer line</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-1" />
                    <span>Digital recording emailed for HOA documentation</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-1" />
                    <span>Written report with findings and recommendations</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-1" />
                    <span>Location marking of problem areas</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-1" />
                    <span>Affordable repair options if issues found</span>
                  </li>
                </ul>
              </div>

              <div className="mt-6 bg-white/5 rounded-lg p-6">
                <h3 className="text-xl font-bold mb-4 text-green-400">Prevention Saves Thousands in Highlands Ranch</h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-blue-200">Preventive Inspection:</span>
                    <span className="text-green-400 font-bold">$175</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-blue-200">Emergency Spot Repair:</span>
                    <span className="text-yellow-300 font-bold">$5,000</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-blue-200">Emergency Full Replacement:</span>
                    <span className="text-red-400 font-bold">$12,000</span>
                  </div>
                </div>
                <p className="text-blue-100 mt-4">
                  Annual inspections catch problems early when affordable repairs are still possible.
                </p>
                <Link to="/diagnostic-services-pricing" className="inline-flex items-center text-yellow-400 hover:text-yellow-300 font-semibold mt-4">
                  Schedule Preventive Inspection →
                </Link>
              </div>

              <div className="mt-6 bg-white/5 rounded-lg p-6">
                <h3 className="text-xl font-bold mb-4 text-blue-400">HOA Compliance and Home Sales</h3>
                <p className="text-blue-100">
                  Many Highlands Ranch HOAs require sewer inspections during home sales. Our affordable $175 inspection provides official documentation, preventing sale delays. Eastridge and The Ranch areas with mature trees particularly benefit from regular inspections to catch root intrusion early.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ 5: Emergency Water Heater - Dark Gradient */}
        <section className="py-16 bg-gradient-to-br from-blue-800 to-blue-900 overflow-hidden">
          <div className="container-custom max-w-4xl relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
              How Quickly Can Colorado First Provide Affordable Emergency Water Heater Replacement?
            </h2>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20">
              <p className="text-lg mb-6 text-blue-100">
                <strong className="text-yellow-300">Colorado First Plumbing and Sewer LLC offers same-day affordable water heater replacement in Highlands Ranch with transparent pricing.</strong> Our $2,000 for a 40-gallon unit means you know the exact cost before we arrive - proving affordable means transparent pricing with no hidden fees or surprise charges.
              </p>
              <img 
                src="/page-specific-content/highlands-ranch-plumber/we-come-prepared-with-fully-stocked-truck-and-trailer.jpg" 
                alt="Colorado First Plumbing fully stocked truck ready for emergency water heater replacement in Highlands Ranch"
                className="w-full rounded-lg mb-6 shadow-lg"
                loading="lazy"
              />
              
              <div className="mt-8 bg-white/5 rounded-lg p-6">
                <h3 className="text-xl font-bold mb-4 text-yellow-400">Same-Day Service with Affordable Transparent Pricing</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-white font-bold mb-2">Emergency Response Times:</p>
                    <ul className="text-blue-100 space-y-1">
                      <li>• Morning calls: Same day installation</li>
                      <li>• Afternoon calls: Same or next day</li>
                      <li>• Weekend available (Sat 8-5)</li>
                      <li>• Fully stocked trucks ready</li>
                    </ul>
                  </div>
                  <div>
                    <p className="text-white font-bold mb-2">Transparent Pricing:</p>
                    <ul className="text-blue-100 space-y-1">
                      <li>• 40-gallon unit: <span className="text-yellow-300 font-bold">$2,000</span></li>
                      <li>• 50-gallon unit: <span className="text-yellow-300 font-bold">$2,100</span></li>
                      <li>• All costs known upfront</li>
                      <li>• No hidden fees or surprises</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="mt-6 bg-white/5 rounded-lg p-6">
                <h3 className="text-xl font-bold mb-4 text-green-400">Quick Access Throughout Highlands Ranch</h3>
                <p className="text-blue-100 mb-4">
                  Our central location near C-470 provides fast response to all Highlands Ranch neighborhoods. From Backcountry to Sterling Ranch, we arrive quickly with fully stocked trucks carrying Rheem and AO Smith units for immediate installation. No waiting for parts or return trips.
                </p>
                <Link to="/emergency-success" className="inline-flex items-center text-yellow-400 hover:text-yellow-300 font-semibold">
                  Learn About Emergency Service →
                </Link>
              </div>

              <div className="mt-6 bg-white/5 rounded-lg p-6">
                                              </div>
            </div>
          </div>
        </section>

        {/* FAQ 6: Sewer Cleaning Methods - Light Gradient */}
        <section className="py-16 bg-gradient-to-bl from-blue-900 to-blue-950 overflow-hidden">
          <div className="container-custom max-w-4xl relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
              What's the Most Affordable Sewer Cleaning Method for Highlands Ranch?
            </h2>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20">
              <p className="text-lg mb-6 text-blue-100">
                <strong className="text-yellow-300">Colorado First Plumbing and Sewer LLC offers affordable sewer cleaning starting at $295 for standard snaking through cleanout access or $695 for powerful hydro jetting.</strong> For Highlands Ranch's mix of newer PVC pipes in Sterling Ranch and older clay lines in The Ranch, we recommend the most cost-effective solution based on your specific situation - always with transparent, upfront pricing.
              </p>
              <img 
                src="/page-specific-content/highlands-ranch-plumber/found-corroded-cast-iron-pipe-in-highlands-ranch.JPEG" 
                alt="Corroded pipe found during sewer cleaning requiring affordable repair solution in Highlands Ranch"
                className="w-full rounded-lg mb-6 shadow-lg"
                loading="lazy"
              />
              
              <div className="mt-8 bg-white/5 rounded-lg p-6">
                <h3 className="text-xl font-bold mb-4 text-yellow-400">Affordable Cleaning Options by Situation</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-blue-800/50 rounded-lg p-4">
                    <h4 className="text-white font-bold mb-2">Standard Snaking ($295-$695)</h4>
                    <p className="text-blue-200 text-sm mb-2">Best for:</p>
                    <ul className="text-blue-100 space-y-1 text-sm">
                      <li>• Simple clogs and blockages</li>
                      <li>• Regular maintenance</li>
                      <li>• Newer PVC pipes</li>
                      <li>• Budget-conscious solution</li>
                    </ul>
                  </div>
                  <div className="bg-green-800/50 rounded-lg p-4">
                    <h4 className="text-white font-bold mb-2">Hydro Jetting ($695)</h4>
                    <p className="text-blue-200 text-sm mb-2">Best for:</p>
                    <ul className="text-blue-100 space-y-1 text-sm">
                      <li>• Grease buildup removal</li>
                      <li>• Tree root cutting</li>
                      <li>• Clay pipe restoration</li>
                      <li>• Long-term prevention</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="mt-6 bg-white/5 rounded-lg p-6">
                <h3 className="text-xl font-bold mb-4 text-green-400">Preventive Maintenance Schedules Save Money</h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-blue-200">Annual Cleaning (Prevention):</span>
                    <span className="text-green-400 font-bold">$295/year</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-blue-200">Emergency Weekend Call:</span>
                    <span className="text-yellow-300 font-bold">$495+</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-blue-200">Neglected Line Repair:</span>
                    <span className="text-red-400 font-bold">$5,000+</span>
                  </div>
                </div>
                <p className="text-blue-100 mt-4">
                  Regular affordable maintenance prevents expensive emergencies.
                </p>
                <Link to="/sewer-cleaning-pricing" className="inline-flex items-center text-yellow-400 hover:text-yellow-300 font-semibold mt-4">
                  View All Cleaning Options →
                </Link>
              </div>

              <div className="mt-6 bg-white/5 rounded-lg p-6">
                <h3 className="text-xl font-bold mb-4 text-blue-400">Neighborhood-Specific Recommendations</h3>
                <p className="text-blue-100">
                  Firelight and Sterling Ranch with newer construction benefit from basic annual snaking at $295. Established neighborhoods like Eastridge and The Ranch with mature trees often require hydro jetting at $695 to cut through root intrusion. Our affordable pricing ensures you get the right service without overpaying.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Common Issues Section - Dark Gradient */}
        <section className="py-16 bg-gradient-to-br from-blue-800 to-blue-900 overflow-hidden">
          <div className="container-custom relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-white">
              Common Plumbing Issues in Highlands Ranch Neighborhoods
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                <div className="flex items-center mb-4">
                  <AlertTriangle className="w-8 h-8 text-yellow-400 mr-3" />
                  <h3 className="text-xl font-bold text-white">Tree Root Invasion (Eastridge & The Ranch)</h3>
                </div>
                <p className="text-blue-100 mb-4">
                  Mature cottonwoods and willows in established neighborhoods aggressively seek water in sewer lines. Common in areas developed before 2000 with clay pipes.
                </p>
                <p className="text-sm text-yellow-300">
                  Affordable Solution: Root removal $695, preventive camera inspection $175.
                </p>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                <div className="flex items-center mb-4">
                  <Droplet className="w-8 h-8 text-blue-400 mr-3" />
                  <h3 className="text-xl font-bold text-white">HOA Compliance Issues</h3>
                </div>
                <p className="text-blue-100 mb-4">
                  Strict landscaping requirements mean traditional excavation can result in fines. Backcountry and Sterling Ranch HOAs require pre-approval for major work.
                </p>
                <p className="text-sm text-yellow-300">
                  Affordable Solution: Trenchless repairs minimize disruption. We handle HOA documentation.
                </p>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                <div className="flex items-center mb-4">
                  <Gauge className="w-8 h-8 text-red-400 mr-3" />
                  <h3 className="text-xl font-bold text-white">Altitude Water Heater Issues</h3>
                </div>
                <p className="text-blue-100 mb-4">
                  At 5,900 feet, water heaters lose 4% efficiency per 1,000 feet. Improper installation leads to poor performance and premature failure.
                </p>
                <p className="text-sm text-yellow-300">
                  Affordable Solution: Professional installation with altitude adjustments, $2,000-$2,100 summer special.
                </p>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                <div className="flex items-center mb-4">
                  <AlertTriangle className="w-8 h-8 text-orange-400 mr-3" />
                  <h3 className="text-xl font-bold text-white">Smart Home Integration (Sterling Ranch)</h3>
                </div>
                <p className="text-blue-100 mb-4">
                  Newer neighborhoods with smart home systems require compatible plumbing fixtures. Leak detection systems need professional integration.
                </p>
                <p className="text-sm text-yellow-300">
                  Affordable Solution: Smart-compatible installations with transparent pricing.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Licensed Plumber Importance - Light Gradient */}
        <section className="py-16 bg-gradient-to-bl from-blue-900 to-blue-950 overflow-hidden">
          <div className="container-custom relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-white">
              Why Choose a Licensed Master Plumber in Highlands Ranch?
            </h2>
            <div className="max-w-4xl mx-auto">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20 mb-8">
                <h3 className="text-2xl font-bold mb-6 text-yellow-400">Affordable Doesn't Mean Unlicensed</h3>
                <p className="text-blue-100 mb-4">
                  Colorado First Plumbing and Sewer LLC operates under Master Plumber Codee Jessen's license, ensuring all work meets Douglas County codes while maintaining affordable pricing. Licensed doesn't mean expensive - it means professional, safe, and guaranteed.
                </p>
                <ul className="space-y-3 text-blue-100">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-1" />
                    <span>Douglas County permit expertise</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-1" />
                    <span>HOA compliance documentation</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-1" />
                    <span>Altitude-adjusted installations</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-1" />
                                      </li>
                </ul>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20 mb-8">
                <h3 className="text-2xl font-bold mb-6 text-green-400">Professional Equipment at Affordable Rates</h3>
                <p className="text-blue-100 mb-4">
                  Our investment in professional equipment means efficient, affordable service:
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-blue-100">
                  <div className="bg-white/5 rounded-lg p-4">
                    <strong className="text-white">Diagnostic Tools:</strong>
                    <ul className="mt-2 space-y-1 text-sm">
                      <li>• HD sewer cameras ($175 inspection)</li>
                      <li>• Electronic leak detection</li>
                      <li>• Pipe locating equipment</li>
                    </ul>
                  </div>
                  <div className="bg-white/5 rounded-lg p-4">
                    <strong className="text-white">Repair Equipment:</strong>
                    <ul className="mt-2 space-y-1 text-sm">
                      <li>• Hydro jetting machines</li>
                      <li>• Trenchless repair systems</li>
                      <li>• Professional drain machines</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20">
                <h3 className="text-2xl font-bold mb-6 text-blue-400">What Makes Us Truly Affordable?</h3>
                <div className="space-y-4 text-blue-100">
                  <div className="flex items-start gap-3">
                    <span className="text-2xl">✅</span>
                    <div>
                      <strong className="text-white">No Sales Commissions:</strong> Our plumbers earn honest wages, not sales bonuses
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-2xl">✅</span>
                    <div>
                      <strong className="text-white">Efficient Operations:</strong> Family-owned means lower overhead
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-2xl">✅</span>
                    <div>
                      <strong className="text-white">Volume Purchasing:</strong> 18,000+ jobs means better material costs
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-2xl">✅</span>
                    <div>
                      <strong className="text-white">Transparent Pricing:</strong> No time wasted on estimates and negotiations
                    </div>
                  </div>
                </div>
                <p className="mt-6 text-yellow-300 font-semibold">
                  Colorado First Plumbing proves affordable means honest with <Link to="/reviews" className="text-blue-200 hover:text-blue-100 underline">1000+ reviews across all platforms</Link> and exact online pricing.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Price Timestamp - NEW */}
        <section className="py-8 bg-gradient-to-br from-blue-800 to-blue-900">
          <div className="container-custom text-center">
            <div className="inline-flex items-center bg-white/10 backdrop-blur-sm rounded-full px-6 py-3 border border-white/20">
              <CheckCircle className="w-5 h-5 text-green-400 mr-2" />
              <span className="text-white font-semibold">
                ✅ Affordable Prices Verified: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
              </span>
            </div>
          </div>
        </section>

        {/* Final CTA Section - Dark Gradient */}
        <section className="py-20 bg-gradient-to-br from-blue-800 to-blue-900 overflow-hidden">
          <div className="container-custom text-center relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
              Ready for Truly Affordable Plumbing Service in Highlands Ranch?
            </h2>
            <p className="text-xl mb-8 text-blue-100 max-w-3xl mx-auto">
              Stop wondering what "affordable" means. See exact prices online, no hidden fees, no surprises.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto mb-12">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                <div className="text-3xl font-bold text-orange-400 mb-2">$5,000</div>
                <p className="text-white font-semibold">Tankless Replace</p>
                <p className="text-sm text-blue-100">Existing unit replacement</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                <div className="text-3xl font-bold text-green-400 mb-2">$2,000</div>
                <p className="text-white font-semibold">40 Gal Water Heater</p>
                <p className="text-sm text-blue-100">Affordable summer special</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                <div className="text-3xl font-bold text-blue-400 mb-2">$175</div>
                <p className="text-white font-semibold">Sewer Camera Scope</p>
                <p className="text-sm text-blue-100">Prevention saves thousands</p>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              
              <Link 
                to="/pricing" 
                className="bg-gradient-to-r from-success-primary to-success-eco hover:shadow-xl hover:scale-[1.02] text-white px-10 py-5 rounded-full text-xl font-semibold inline-block transition-all duration-300"
              >
                💰 View All Affordable Pricing
              </Link>
            </div>
            
            <div className="mt-8 flex flex-wrap justify-center gap-6 text-white/80">
              <span className="flex items-center gap-2">
                <MapPin className="w-5 h-5" />
                Serving all Highlands Ranch neighborhoods
              </span>
              <span className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Mon-Sat 8AM-5PM
              </span>
              <span className="flex items-center gap-2">
                <Award className="w-5 h-5" />
                Licensed & Insured
              </span>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default HighlandsRanchPlumber;